import api from './server'
import type {
  BulkRightsSplitsRequest,
  BulkRightsSplitsResponse,
  CreateRightsHolderRequest,
  RightsData,
  RightsHolderEntity,
  RightsHoldersQueryParams,
  RightsSplitChain,
  RightsSplitChainRequest,
  RightsType,
  ReplaceRightsSplitsRequest,
  UpdateRightsHolderRequest,
} from 'types/rights-data'
import type { TrackId } from 'types/track'

/**
 * Request payload for creating/updating writer splits via POST /api/tracks/{track_id}/writer-splits/
 */
export type WriterSplitsRequest = {
  splits: Array<
    | {
        writer: number
        publisher: number | null
        split_percentage: number
        pro: number | null
      }
    | {
        rightsholder: number
        label: number | null
        split_percentage: number
      }
  >
}

/**
 * Writer split item in the API response
 */
export type WriterSplitResponseItem = {
  id: number
  writer_id: number
  writer: {
    id: number
    name: string
  }
  publisher_id: number | null
  publisher: {
    id: number
    name: string
  } | null
  split_percentage: number
  pro_id: number | null
  pro: {
    id: number
    name: string
  } | null
}

/**
 * Response from POST /api/tracks/{track_id}/writer-splits/
 */
export type WriterSplitsResponse = {
  track_id: TrackId
  splits: WriterSplitResponseItem[]
}

export const requestRightsData: (
  trackId: number,
) => Promise<RightsData> = trackId =>
  api.getUrl(
    `${api.host}/api/rights_data/track/${trackId}`,
    null,
    'api.requestRightsData',
  )

export const provideRightsDataFeedback: (
  isHelpful: boolean,
  comment: string | undefined,
  rightsDataUsage: number | undefined,
) => Promise<any> = (isHelpful, comment, rightsDataUsage) => {
  const payload = { is_helpful: isHelpful }
  if (comment) {
    payload['comment'] = comment
  }
  if (rightsDataUsage) {
    payload['usage'] = rightsDataUsage
  }

  return api.postUrl(
    `${api.host}/api/rights_data/feedback/`,
    payload,
    'api.provideRightsDataFeedback',
  )
}
/**
 * Saves writer splits for a track.
 * Creates/updates multiple writer splits in a single request.
 * @param trackId - The track ID to save splits for
 * @param data - The splits data to save
 * @returns Promise resolving to the saved splits with server-generated IDs
 */
export const saveWriterSplits: (
  trackId: number,
  data: WriterSplitsRequest,
) => Promise<WriterSplitsResponse> = (trackId, data) =>
  api.postUrl(
    `${api.host}/api/track/${trackId}/writer_splits/`,
    data,
    'api.saveWriterSplits',
  )

/**
 * Request payload for batch saving writer splits via POST /api/track/batch_writer_splits/
 */
export type BatchWriterSplitsRequest = {
  track_ids: number[]
  splits: Array<{
    writer: number
    publisher: number | null
    split_percentage: number
    pro: number | null
  }>
  /** When false, splits are appended to existing splits (append mode) */
  overwrite: boolean
}

/**
 * Error item for a single track in a batch operation
 */
export type BatchWriterSplitsErrorItem = {
  track_id: number | string // API returns as string in error responses
  message: string
}

/**
 * Success item for a single track in a batch operation
 */
export type BatchWriterSplitsTrackResult = {
  track_id: number
  splits: WriterSplitResponseItem[]
}

/**
 * Error response from POST /api/track/batch_writer_splits/
 * Returned when validation fails (e.g., total splits exceed 100%)
 */
export type BatchWriterSplitsErrorResponse = {
  splits: BatchWriterSplitsErrorItem[]
}

/**
 * Success response from POST /api/track/batch_writer_splits/
 * Returned when all tracks are successfully updated
 */
export type BatchWriterSplitsSuccessResponse = {
  tracks: BatchWriterSplitsTrackResult[]
}

/**
 * Response from POST /api/track/batch_writer_splits/
 * Returns either a success response with tracks array, or an error response with splits array
 */
export type BatchWriterSplitsResponse =
  | BatchWriterSplitsSuccessResponse
  | BatchWriterSplitsErrorResponse

/**
 * Type guard to check if response is a success response
 */
export const isBatchWriterSplitsSuccessResponse = (
  response: BatchWriterSplitsResponse,
): response is BatchWriterSplitsSuccessResponse => {
  return 'tracks' in response
}

/**
 * Type guard to check if response is an error response
 */
export const isBatchWriterSplitsErrorResponse = (
  response: BatchWriterSplitsResponse,
): response is BatchWriterSplitsErrorResponse => {
  return 'splits' in response && !('tracks' in response)
}

/**
 * Saves writer splits for multiple tracks in a single batch request. Supports both overwrite and append modes.
 * POST /api/track/batch_writer_splits/
 */
export const batchSaveWriterSplits: (
  data: BatchWriterSplitsRequest,
) => Promise<BatchWriterSplitsResponse> = data =>
  api.postUrl(
    `${api.host}/api/track/batch_writer_splits/`,
    data,
    'api.batchSaveWriterSplits',
  )

/**
 * Fetches rights holders with optional filtering.
 * GET /api/rights_data/rights_holders/
 */
export const fetchRightsHolders = (
  params: RightsHoldersQueryParams,
): Promise<RightsHolderEntity[]> =>
  api.getUrl(
    `${api.host}/api/rights_data/rights_holders/`,
    params,
    'api.fetchRightsHolders',
  )

/**
 * Creates a new rights holder entity.
 * POST /api/rights_data/rights_holders/
 */
export const createRightsHolder = (
  data: CreateRightsHolderRequest,
): Promise<RightsHolderEntity> =>
  api.postUrl(
    `${api.host}/api/rights_data/rights_holders/`,
    data,
    'api.createRightsHolder',
  )

/**
 * Updates an existing rights holder entity.
 * PATCH /api/rights_data/rights_holders/{id}/
 */
export const updateRightsHolder = (
  id: number,
  data: UpdateRightsHolderRequest,
): Promise<RightsHolderEntity> =>
  api.patchUrl(
    `${api.host}/api/rights_data/rights_holders/${id}/`,
    data,
    'api.updateRightsHolder',
  )

/**
 * Deletes a rights holder entity.
 * DELETE /api/rights_data/rights_holders/{id}/
 */
export const deleteRightsHolder = (id: number): Promise<void> =>
  api.delUrl(
    `${api.host}/api/rights_data/rights_holders/${id}/`,
    'api.deleteRightsHolder',
  )

/**
 * Gets all rights split chains for a track.
 * GET /api/rights_data/tracks/{track_id}/rights_splits/
 */
export const getTrackRightsSplits = (
  trackId: number,
  rightsType?: RightsType,
  territory?: string,
): Promise<RightsSplitChain[]> => {
  const params: Record<string, string> = {}
  if (rightsType) params.rights_type = rightsType
  if (territory !== undefined) params.territory = territory

  return api.getUrl(
    `${api.host}/api/rights_data/tracks/${trackId}/rights_splits/`,
    Object.keys(params).length > 0 ? params : null,
    'api.getTrackRightsSplits',
  )
}

/**
 * Replaces all rights split chains for a specific rights type on a track.
 * PUT /api/rights_data/tracks/{track_id}/rights_splits/replace/
 */
export const replaceTrackRightsSplits = (
  trackId: number,
  data: ReplaceRightsSplitsRequest,
): Promise<RightsSplitChain[]> =>
  api.putUrl(
    `${api.host}/api/rights_data/tracks/${trackId}/rights_splits/replace/`,
    data,
    'api.replaceTrackRightsSplits',
  )

/**
 * Bulk create/update rights splits for multiple tracks.
 * POST /api/rights_data/rights_splits/bulk/
 */
export const bulkSaveRightsSplits = (
  data: BulkRightsSplitsRequest,
  atomic = true,
): Promise<BulkRightsSplitsResponse> =>
  api.postUrl(
    `${api.host}/api/rights_data/rights_splits/bulk/${
      atomic ? '' : '?atomic=false'
    }`,
    data,
    'api.bulkSaveRightsSplits',
  )
